import { Component, Input, OnChanges, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngxs/store";
import { PageModel } from "../report-page-state/report-page.state-models";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";
import { Snackbar } from "@core/material";
import { CultevaGridsterConfig, LANDSCAPE_PAGE_OPTIONS, PAGE_OPTION_DEFAULT, PageOptionConfig, PageOrientation, PORTRAIT_PAGE_OPTIONS, ResolvedTypography, TypographySettings } from "@core/data/types/advanced-report";
import { EditPageOptions } from "../report-page-state/report-page.state-actions";
import { resolveTypography } from "@core/utils";
import { AdvancedReportState } from "../../report-view/report-state/advanced-report.state";

@Component({
    selector: 'pv-page-settings',
    templateUrl: 'report-page-settings.component.html',
    styleUrls: ['report-page-settings.component.scss'],
    host: {
        class: 'pv-page-settings'
    }
})
export class PvPageSettings implements OnInit, OnDestroy {

    @Input()
    model: PageModel;

    // A page's orientation can be set independently of the report; EditPageOptions clamps any
    // component that no longer fits the new grid instead of orphaning it (see report-page.state.ts).
    @Input()
    showOrientation = true;

    orientationConrol: FormControl = new FormControl(null, [Validators.required]);

    formGroup: FormGroup = new FormGroup({
        pageOrientation: this.orientationConrol,
    });

    pageTypography: TypographySettings = {};

    // Resolved report typography — the inherited baseline the page's overrides cascade over. Read live
    // (not snapshotted in ngOnInit) so the "From report" preview tracks report-font edits made while
    // this panel is open.
    get parentTypography(): ResolvedTypography {
        const reportConfig = this._store.selectSnapshot(AdvancedReportState.getReport(this.model.reportKey))?.report?.options?.config;
        return resolveTypography([reportConfig?.inherited?.typography]);
    }

    private _destroy$ = new Subject
    ();

    constructor(
        private _store: Store,
        private _snackbar: Snackbar,
    ) {}

    ngOnInit() {

        // model.page can be null (page still loading after creation) and model.page.options can be
        // null (options created via the AddReportPageOptions reactive fallback, not yet synced back
        // into PageState) — fall back to the report default rather than throwing in either case.
        const config = this.model.page?.options?.config;
        const pageOrientation = config?.pageOrientation ?? PAGE_OPTION_DEFAULT.pageOrientation;

        this.pageTypography = config?.inherited?.typography ?? {};

        this.formGroup.reset({
            pageOrientation: pageOrientation,
        }, { emitEvent: false });

        this.formGroup.valueChanges
            .pipe(
                takeUntil(this._destroy$),
                distinctUntilChanged(),
                debounceTime(300)
            ).subscribe(() => {
                this.attempt();
            });
    }

    attempt() {
        this.formGroup.markAsDirty();
        this.formGroup.markAllAsTouched();

        if (!this.formGroup.valid) return this._snackbar.error('Invalid data, check your inputs and try again.');

        // Nothing to update yet — the page's options are still loading/being created (see ngOnInit's
        // fallback). The edit that triggered this will be lost; that's acceptable, the alternative is
        // throwing on a page that isn't ready to be edited yet.
        if (!this.model.page?.options) return;

        const form = this.formGroup.value;

        const { config } = this.model.page.options;
        let pageOrientation = form.pageOrientation as PageOrientation;

        let pageSize: Partial<CultevaGridsterConfig>;

        if (pageOrientation == 'portrait') pageSize = PORTRAIT_PAGE_OPTIONS;
        else pageSize = LANDSCAPE_PAGE_OPTIONS;

        const options: PageOptionConfig = {
            ...config,
            gridOptions: {
                ...config.gridOptions,
                ...pageSize,
            },
            pageOrientation: pageOrientation,
        }

        this._store.dispatch(new EditPageOptions(this.model.page.options.key, this.model.key, options));
    }

    onTypographyChange(typography: TypographySettings) {
        this.pageTypography = typography;

        // Same as attempt() — nothing to update yet if the page's options haven't loaded/been created.
        if (!this.model.page?.options) return;

        const { config } = this.model.page.options;
        const options: PageOptionConfig = {
            ...config,
            inherited: {
                ...config.inherited,
                typography: typography,
            },
        };

        this._store.dispatch(new EditPageOptions(this.model.page.options.key, this.model.key, options));
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
}